import React, { useState } from 'react';
import { Archive, ArchiveRestore, Check, Pencil, Plus, X } from 'lucide-react';
import { roomLabel, BUSINESSES, BUSINESS_SHORT } from '@/lib/milliData';
import { DEPARTMENTS, PILLARS } from '@/lib/milliDepartments';

/* Left rail of Milli's console: the rooms (one transcript each, shared
 * memory) and the desks (the 15 departments, lit by whichever answered
 * last). Every action here goes through the chat hook, which talks to the
 * owner-only room RPCs. */

const fmtWhen = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const input =
  'w-full rounded-md bg-[#17181a] border border-[#3a332a] px-2.5 py-1.5 text-sm text-stone-200 ' +
  'placeholder:text-stone-600 focus:outline-none focus:border-[color:var(--milli-accent)]';
const select =
  'rounded-md bg-[#17181a] border border-[#3a332a] px-2 py-1.5 font-mono text-[11px] text-stone-300 ' +
  'focus:outline-none focus:border-[color:var(--milli-accent)] disabled:opacity-60';
const iconBtn =
  'rounded border border-[#3a332a] p-1.5 text-stone-400 hover:text-[color:var(--milli-accent)] ' +
  'hover:border-[color:var(--milli-accent)] disabled:opacity-40 transition-colors';

function RailTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between px-3 pt-4 pb-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-500">{children}</span>
      {action}
    </div>
  );
}

export default function RoomsRail({ chat, t, onPicked }) {
  const {
    rooms, sessionId, currentRoom, thinking, showArchived, department,
    switchRoom, createRoom, renameRoom, changeBusiness, toggleArchive, toggleShowArchived,
  } = chat;
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBusiness, setNewBusiness] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const create = async () => {
    const id = await createRoom(newTitle, newBusiness);
    if (id) {
      setNewOpen(false);
      setNewTitle('');
      setNewBusiness('');
      onPicked?.();
    }
  };

  const saveRename = async () => {
    if (await renameRoom(renameValue)) setRenaming(false);
  };

  const pick = async (id) => {
    await switchRoom(id);
    onPicked?.();
  };

  const active = new Set([department?.id, department?.secondary?.id].filter(Boolean));
  const live = rooms.filter((r) => !r.archived_at);
  const archived = rooms.filter((r) => r.archived_at);

  return (
    <div className="flex h-full flex-col overflow-y-auto" data-testid="milli-console-rail">
      {/* Rooms */}
      <RailTitle
        action={
          <button
            type="button"
            onClick={() => setNewOpen((v) => !v)}
            className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-stone-400 hover:text-[color:var(--milli-accent)] transition-colors"
            data-testid="milli-new-conversation"
          >
            <Plus size={11} />
            {t('milli.rooms.new')}
          </button>
        }
      >
        {t('milli.console.rooms')}
      </RailTitle>

      {newOpen ? (
        <div className="mx-3 mb-2 space-y-2 rounded-lg border border-[#3a332a] bg-[#17181a]/60 p-2.5" data-testid="milli-new-room-form">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') create();
              if (e.key === 'Escape') setNewOpen(false);
            }}
            autoFocus
            maxLength={80}
            placeholder={t('milli.rooms.titlePlaceholder')}
            className={input}
            data-testid="milli-new-room-title"
          />
          <div className="flex items-center gap-2">
            <select
              value={newBusiness}
              onChange={(e) => setNewBusiness(e.target.value)}
              className={`${select} flex-1 min-w-0`}
              data-testid="milli-new-room-business"
            >
              <option value="">{t('milli.rooms.businessAuto')}</option>
              {BUSINESSES.map((b) => (
                <option key={b} value={b}>{BUSINESS_SHORT[b]}</option>
              ))}
            </select>
            <button type="button" onClick={create} className={iconBtn} aria-label={t('milli.rooms.create')} data-testid="milli-new-room-create">
              <Check size={12} />
            </button>
            <button type="button" onClick={() => setNewOpen(false)} className={iconBtn} aria-label={t('milli.rooms.cancel')}>
              <X size={12} />
            </button>
          </div>
          <p className="text-[11px] text-stone-500">{t('milli.rooms.hint')}</p>
        </div>
      ) : null}

      <ul className="px-2 space-y-0.5" data-testid="milli-room-list">
        {sessionId && !currentRoom ? (
          <li>
            <RoomButton active label={t('milli.rooms.untitled')} />
          </li>
        ) : null}
        {live.map((r) => (
          <li key={r.session_id}>
            <RoomButton
              active={r.session_id === sessionId}
              label={r.title || r.preview || t('milli.rooms.untitled')}
              tag={r.business ? BUSINESS_SHORT[r.business] ?? r.business : null}
              when={fmtWhen(r.updated_at)}
              disabled={thinking}
              onClick={() => pick(r.session_id)}
              testId={`milli-room-${r.session_id}`}
            />
          </li>
        ))}
        {showArchived && archived.length ? (
          <>
            <li className="px-2 pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-600">
              {t('milli.rooms.archived')}
            </li>
            {archived.map((r) => (
              <li key={r.session_id}>
                <RoomButton
                  active={r.session_id === sessionId}
                  label={roomLabel(r, t('milli.rooms.untitled'))}
                  when={fmtWhen(r.updated_at)}
                  disabled={thinking}
                  muted
                  onClick={() => pick(r.session_id)}
                  testId={`milli-room-${r.session_id}`}
                />
              </li>
            ))}
          </>
        ) : null}
      </ul>
      <button
        type="button"
        onClick={toggleShowArchived}
        className={`mx-3 mt-2 text-left font-mono text-[10px] uppercase tracking-wider transition-colors ${showArchived ? 'text-[color:var(--milli-accent)]' : 'text-stone-600 hover:text-stone-400'}`}
        data-testid="milli-room-show-archived"
      >
        {t('milli.rooms.showArchived')}
      </button>

      {/* This room */}
      {sessionId ? (
        <div className="mx-3 mt-4 rounded-lg border border-[#3a332a] p-2.5 space-y-2" data-testid="milli-room-bar">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{t('milli.console.thisRoom')}</div>
          {renaming ? (
            <div className="flex items-center gap-1.5">
              <input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRename();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                autoFocus
                maxLength={80}
                className={input}
                data-testid="milli-room-rename"
              />
              <button type="button" onClick={saveRename} className={iconBtn} aria-label={t('milli.rooms.save')}>
                <Check size={12} />
              </button>
              <button type="button" onClick={() => setRenaming(false)} className={iconBtn} aria-label={t('milli.rooms.cancel')}>
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="flex-1 truncate text-sm text-stone-200" title={currentRoom?.title || ''}>
                {currentRoom?.title || currentRoom?.preview || t('milli.rooms.untitled')}
              </span>
              <button
                type="button"
                onClick={() => {
                  setRenameValue(currentRoom?.title ?? '');
                  setRenaming(true);
                }}
                className={iconBtn}
                aria-label={t('milli.rooms.rename')}
                title={t('milli.rooms.rename')}
                data-testid="milli-room-rename-btn"
              >
                <Pencil size={12} />
              </button>
              <button
                type="button"
                onClick={toggleArchive}
                disabled={thinking}
                className={iconBtn}
                aria-label={currentRoom?.archived_at ? t('milli.rooms.unarchive') : t('milli.rooms.archive')}
                title={currentRoom?.archived_at ? t('milli.rooms.unarchive') : t('milli.rooms.archive')}
                data-testid="milli-room-archive"
              >
                {currentRoom?.archived_at ? <ArchiveRestore size={12} /> : <Archive size={12} />}
              </button>
            </div>
          )}
          <select
            value={currentRoom?.business ?? ''}
            onChange={(e) => changeBusiness(e.target.value)}
            disabled={thinking}
            title={t('milli.rooms.businessAuto')}
            className={`${select} w-full`}
            data-testid="milli-room-business"
          >
            <option value="">{t('milli.rooms.businessAuto')}</option>
            {BUSINESSES.map((b) => (
              <option key={b} value={b}>{BUSINESS_SHORT[b]}</option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Desks */}
      <RailTitle>{t('milli.console.desks')}</RailTitle>
      <div className="px-3 pb-6 space-y-3" data-testid="milli-desks">
        {PILLARS.map((p) => (
          <div key={p.id}>
            <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-stone-600">{p.name}</div>
            <ul className="space-y-0.5">
              {p.departments.map((id) => {
                const d = DEPARTMENTS[id];
                const on = active.has(id);
                return (
                  <li
                    key={id}
                    className={`flex items-center gap-2 rounded px-1.5 py-0.5 text-[12px] transition-colors ${on ? 'bg-[#1e1f21] text-stone-100' : 'text-stone-500'}`}
                    data-testid={`milli-desk-${id}`}
                    data-active={on ? 'true' : 'false'}
                  >
                    <span
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ background: d.hex, opacity: on ? 1 : 0.45, boxShadow: on ? `0 0 8px ${d.hex}` : 'none' }}
                    />
                    <span className="truncate">{d.name}</span>
                    {on && department?.id === id ? (
                      <span className="ml-auto font-mono text-[9px] uppercase tracking-wider" style={{ color: d.hex }}>
                        {t('milli.console.speaking')}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <p className="text-[11px] leading-snug text-stone-600">{t('milli.console.desksHint')}</p>
      </div>
    </div>
  );
}

function RoomButton({ active, label, tag, when, disabled, muted, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || !onClick}
      className={`w-full rounded-md px-2.5 py-2 text-left transition-colors border-l-2 ${
        active
          ? 'bg-[#1e1f21] border-[color:var(--milli-accent)]'
          : 'border-transparent hover:bg-[#1a1b1d]'
      } disabled:opacity-60`}
      data-testid={testId}
      aria-current={active ? 'true' : undefined}
    >
      <div className="flex items-center gap-2">
        <span className={`flex-1 truncate text-[13px] ${muted ? 'text-stone-500' : active ? 'text-stone-100' : 'text-stone-300'}`}>
          {label}
        </span>
        {when ? <span className="shrink-0 font-mono text-[10px] text-stone-600">{when}</span> : null}
      </div>
      {tag ? (
        <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">{tag}</div>
      ) : null}
    </button>
  );
}
